/**
 * Thin fetch wrapper. Handles:
 *   - base URL (/api via the Vite proxy in dev, same origin in prod)
 *   - JSON body serialization
 *   - Sends/receives the httpOnly `mc_token` cookie via credentials
 *     include — no token ever touches JS.
 *   - Error normalization: server responds 4xx with {"error": "..."} —
 *     we convert to a thrown ApiError with the message.
 *
 * No retries, no caching — TanStack Query handles that in the call
 * sites that need it.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();
let authGeneration = 0;

export function getAuthGeneration(): number {
  return authGeneration;
}

/** Invalidate responses belonging to a login that has ended or been replaced. */
export function advanceAuthGeneration(): void {
  authGeneration += 1;
}

/** Login/register/status 401 is "bad credentials" / public, not session expiry. */
const UNAUTHORIZED_EXEMPT = new Set(['/auth/login', '/auth/register', '/auth/status']);

/** AuthProvider drops the session when any cookie-authed call gets 401. */
export function subscribeUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => {
    unauthorizedListeners.delete(fn);
  };
}

function emitUnauthorized(path: string, status: number, generation: number): void {
  if (generation !== authGeneration || status !== 401 || UNAUTHORIZED_EXEMPT.has(path)) return;
  for (const fn of unauthorizedListeners) fn();
}

type ApiOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  signal?: AbortSignal;
};

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const generation = authGeneration;
  const headers: Record<string, string> = {};

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(`/api${path}`, init);
  const text = await res.text();
  let json: unknown = null;
  if (text.length > 0) {
    try {
      json = JSON.parse(text);
    } catch {
      emitUnauthorized(path, res.status, generation);
      throw new ApiError(res.status, `Некорректный ответ сервера: ${text.slice(0, 120)}`);
    }
  }

  if (!res.ok) {
    const message =
      json && typeof json === 'object' && 'error' in json
        ? String((json as { error: string }).error)
        : `Запрос не выполнен: HTTP ${res.status}`;
    emitUnauthorized(path, res.status, generation);
    throw new ApiError(res.status, message);
  }

  return json as T;
}
